import { useState, useEffect, useCallback } from 'react';

/**
 * Hook para gerenciar as permissões de contrato do usuário atual.
 * 
 * @returns {Object} Objeto contendo:
 *   - permissions: objeto com as permissões de contrato
 *   - allowedStatuses: array com os status permitidos (null = todos)
 *   - loading: boolean indicando se está carregando
 *   - error: string com mensagem de erro, se houver
 *   - refreshPermissions: função para recarregar as permissões
 */
const useContractPermissions = () => {
  const [permissions, setPermissions] = useState({
    can_create_contracts: false,
    can_edit_contracts: false,
    can_delete_contracts: false,
    can_file_contracts: false,
    can_restore_contracts: false,
    allow_contracts_without_calc_fields: false,
  });
  const [attendancePermissions, setAttendancePermissions] = useState({
    can_view_attendances: false,
    can_create_attendances: false,
    can_add_attendance_interactions: false,
  });
  const [allowedStatuses, setAllowedStatuses] = useState(null); // null = todos os status
  const [userInfo, setUserInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchPermissions = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await fetch('/backend/authentication/user-info');
      
      if (!response.ok) {
        throw new Error('Não foi possível carregar as permissões do usuário.');
      }
      
      const data = await response.json();
      
      // Superusuários sempre têm todas as permissões e veem todos os status
      if (data.user_info?.is_superuser) {
        setPermissions({
          can_create_contracts: true,
          can_edit_contracts: true,
          can_delete_contracts: true,
          can_file_contracts: true,
          can_restore_contracts: true,
          allow_contracts_without_calc_fields: true,
        });
        setAttendancePermissions({
          can_view_attendances: true,
          can_create_attendances: true,
          can_add_attendance_interactions: true,
        });
        setAllowedStatuses(null); // null = todos os status
      } else {
        if (data.user_info?.contract_permissions) {
          setPermissions(data.user_info.contract_permissions);
        }
        if (data.user_info?.attendance_permissions) {
          setAttendancePermissions(data.user_info.attendance_permissions);
        }
        // Define os status permitidos (null = todos)
        setAllowedStatuses(data.user_info?.allowed_contract_statuses || null);
      }
      
      setUserInfo(data.user_info);
    } catch (err) {
      console.error('Erro ao carregar permissões:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPermissions();
  }, [fetchPermissions]);

  return {
    permissions,
    allowedStatuses,
    userInfo,
    loading,
    error,
    refreshPermissions: fetchPermissions,
    // Helpers para verificar permissões específicas
    canCreate: permissions.can_create_contracts,
    canEdit: permissions.can_edit_contracts,
    canDelete: permissions.can_delete_contracts,
    canFile: permissions.can_file_contracts,
    canRestore: permissions.can_restore_contracts,
    allowWithoutCalcFields: permissions.allow_contracts_without_calc_fields || userInfo?.allow_contracts_without_calc_fields || false,
    canManageUsers: userInfo?.can_manage_users || false,
    canImportData: userInfo?.can_import_data || false,
    canViewCommissionReports: userInfo?.can_view_commission_reports || false,
    canViewAttendances: attendancePermissions.can_view_attendances || userInfo?.is_superuser || false,
    canCreateAttendances: attendancePermissions.can_create_attendances || userInfo?.is_superuser || false,
    canAddAttendanceInteractions: attendancePermissions.can_add_attendance_interactions || userInfo?.is_superuser || false,
    isAdmin: userInfo?.is_staff || userInfo?.is_superuser,
    isSuperuser: userInfo?.is_superuser,
    // Helper para verificar se tem apenas um status permitido
    hasSingleStatus: allowedStatuses !== null && allowedStatuses.length === 1,
    singleStatus: allowedStatuses !== null && allowedStatuses.length === 1 ? allowedStatuses[0] : null,
  };
};

export default useContractPermissions;

